import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { planDocumentAPI } from '../../services/api'

/**
 * VH-236: pick what to add to a plan meal — a food or one of the user's own recipes.
 *
 * A recipe with no nutrition is still listed, greyed out with the reason, so the user sees why it
 * cannot be added yet instead of wondering where it went.
 */
export function PlanItemPicker({ cycleId, planId, language, onPick, onCancel, initialQuantity = 100 }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [quantity, setQuantity] = useState(initialQuantity)
  const search = useQuery({
    queryKey: ['planItemSearch', cycleId, planId, q, language],
    queryFn: () => planDocumentAPI.searchPlanItems(cycleId, planId, q, language).then((r) => r.data),
    enabled: q.trim().length >= 2 && Boolean(cycleId) && Boolean(planId),
  })
  const foods = search.data?.foods ?? []
  const recipes = search.data?.recipes ?? []

  return (
    <div className="border border-[#D8DCD5] rounded-lg p-2 space-y-2 bg-surface-container" data-testid="plan-item-picker">
      <div className="flex flex-wrap gap-2">
        <input
          autoFocus
          className="border border-[#D8DCD5] rounded px-2 py-1 text-sm flex-1 min-w-0"
          placeholder={t('weekPlanning.preview.addPlaceholder')}
          aria-label={t('weekPlanning.preview.addPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input
          type="number"
          min="1"
          max="1500"
          className="border border-[#D8DCD5] rounded px-2 py-1 text-sm w-24"
          aria-label={t('weekPlanning.preview.addQuantity')}
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
        <button type="button" className="text-sm underline" onClick={onCancel}>
          {t('mealPrep.actions.cancel')}
        </button>
      </div>
      {search.isLoading && q.trim().length >= 2 && (
        <p className="text-xs text-gray-500">{t('mealPrep.picker.searching')}</p>
      )}
      {search.isError && <p className="text-xs text-red-600" role="alert">{t('mealPrep.errors.generic')}</p>}
      {search.data && foods.length === 0 && recipes.length === 0 && (
        <p className="text-xs text-gray-500">{t('mealPrep.picker.noResults')}</p>
      )}

      {recipes.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('weekPlanning.preview.recipes')}</p>
          <ul className="space-y-1">
            {recipes.map((recipe) => (
              <li key={`recipe-${recipe.recipe_version_id}`}>
                <button
                  type="button"
                  className="text-sm text-left w-full px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
                  disabled={!recipe.has_nutrition}
                  onClick={() => onPick({ recipe_version_id: recipe.recipe_version_id, grams: quantity })}
                >
                  {recipe.name}
                  {recipe.has_nutrition ? (
                    <span className="text-xs text-gray-500 ml-1">
                      {t('weekPlanning.preview.per100g', { kcal: Math.round(recipe.per_100g.kcal) })}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 ml-1">{t('weekPlanning.preview.noNutrition')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {foods.length > 0 && (
        <>
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{t('weekPlanning.preview.foods')}</p>
          <ul className="space-y-1">
            {foods.map((food) => (
              <li key={`food-${food.id}`}>
                <button
                  type="button"
                  className="text-sm text-left w-full px-2 py-1 rounded hover:bg-gray-100"
                  onClick={() => onPick({ food_id: food.id, grams: quantity })}
                >
                  {food.display_name || food.name}
                  {food.brand ? ` (${food.brand})` : ''}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

export default PlanItemPicker
